import { useState, type ReactNode } from 'react';
import {
  AccountChip,
  Button,
  Cluster,
  Container,
  CreationHeader,
  CreationShell,
  FoldPanel,
  Input,
  Page,
  Specimens,
  Stack,
  Typography,
  UserIcon,
  Well,
} from '@src/components';

const BOX_STEPS = ['Choose game name', 'How you exchange', 'Budget'] as const;
const CARD_STEPS = ['Your name', 'Your picture', 'Your wish', 'Delivery'] as const;
const DRAW_STEPS = ['Names', 'Exceptions'] as const;

function HeaderFrame({ children }: { children: ReactNode }) {
  return (
    <Well>
      <Page flush>{children}</Page>
    </Well>
  );
}

function Account({ name = 'Olga123' }: { name?: string }) {
  return <AccountChip icon={<UserIcon />}>{name}</AccountChip>;
}

function WizardSpecimen({ titles }: { titles: readonly string[] }) {
  const [step, setStep] = useState(1);
  const steps = titles.length;
  const title = titles[step - 1] ?? titles[0];

  return (
    <HeaderFrame>
      <CreationHeader
        sticky={false}
        title={title}
        step={step}
        steps={steps}
        onBack={() => setStep((current) => Math.max(1, current - 1))}
        account={<Account />}
      />
      <Container width="measure" gutter="narrow" pad="page">
        <Cluster gap={3}>
          <Button
            variant="secondary"
            size="small"
            disabled={step <= 1}
            onClick={() => setStep((current) => current - 1)}
          >
            Previous
          </Button>
          <Button
            variant="primary"
            size="small"
            disabled={step >= steps}
            onClick={() => setStep((current) => current + 1)}
          >
            Next
          </Button>
          <Typography as="span" role="meta/helper">
            {title}
          </Typography>
        </Cluster>
      </Container>
    </HeaderFrame>
  );
}

/**
 * Chrome for create-box, create-card and quick draw. A progress track, the
 * current step in the middle, back on the left, the account on the right.
 */
export function CreationHeaderSection() {
  return (
    <FoldPanel
      id="creation-header"
      title="Creation"
      hint="Header plus the wizard shell. Screens pass the step, titles and the form — they do not rebuild the sky, the festive card or the buttons."
      example={`<CreationShell
  heading="Choose game name"
  title="What do you want to name your game?"
  subtitle="Everyone will see the name."
  step={1}
  steps={3}
  onBack={goBack}
  action={<Button>Continue</Button>}
>
  <Input label="Game name" value={name} onValueChange={setName} />
</CreationShell>`}
    >
      <Specimens
        label="Shell"
        note="The create-box / create-card frame. Header, sky, edges, title, decorated card, Back and Continue."
      >
        <Stack gap={4} wide>
          <Well>
            <CreationShell
              contained
              heading="Choose game name"
              title="What do you want to name your game?"
              subtitle="Everyone will see the name — you can always change it later."
              step={1}
              steps={3}
              onBack={() => {}}
              action={<Button>Continue</Button>}
            >
              <Input label="Game name" value="New Year exchange 2026" onValueChange={() => {}} />
            </CreationShell>
          </Well>
        </Stack>
      </Specimens>

      <Specimens
        label="Interactive"
        note="A three-step box create. Previous / Next move the track. Back on the bar does the same."
      >
        <Stack gap={4} wide>
          <WizardSpecimen titles={BOX_STEPS} />
        </Stack>
      </Specimens>

      <Specimens
        label="Steps"
        note="First, middle, last. The fill is the current step over the total, so step 1 of 3 is a third."
      >
        <Stack gap={4} wide>
          <HeaderFrame>
            <CreationHeader
              sticky={false}
              title="Choose game name"
              step={1}
              steps={3}
              onBack={() => {}}
              account={<Account />}
            />
          </HeaderFrame>
          <HeaderFrame>
            <CreationHeader
              sticky={false}
              title="How you exchange"
              step={2}
              steps={3}
              onBack={() => {}}
              account={<Account />}
            />
          </HeaderFrame>
          <HeaderFrame>
            <CreationHeader
              sticky={false}
              title="Budget"
              step={3}
              steps={3}
              onBack={() => {}}
              account={<Account />}
            />
          </HeaderFrame>
        </Stack>
      </Specimens>

      <Specimens
        label="Flows"
        note="Quick draw is two steps. Creating a card can run to four. The header does not care which wizard it is."
      >
        <Stack gap={4} wide>
          <HeaderFrame>
            <CreationHeader
              sticky={false}
              title="Names"
              step={1}
              steps={DRAW_STEPS.length}
              onBack={() => {}}
              account={<Account />}
            />
          </HeaderFrame>
          <HeaderFrame>
            <CreationHeader
              sticky={false}
              title="Your wish"
              step={3}
              steps={CARD_STEPS.length}
              onBack={() => {}}
              account={<Account />}
            />
          </HeaderFrame>
        </Stack>
      </Specimens>

      <Specimens
        label="Back"
        note="Pass onBack for the chevron. Leave it off when the screen has nowhere to go."
      >
        <Stack gap={4} wide>
          <HeaderFrame>
            <CreationHeader
              sticky={false}
              title="Choose game name"
              step={1}
              steps={3}
              account={<Account />}
            />
          </HeaderFrame>
        </Stack>
      </Specimens>

      <Specimens
        label="Account"
        note="The right slot is whatever the shell would put on AppHeader: a chip, a sign-up button, or empty."
      >
        <Stack gap={4} wide>
          <HeaderFrame>
            <CreationHeader
              sticky={false}
              title="Choose game name"
              step={1}
              steps={3}
              onBack={() => {}}
              account={<Account />}
            />
          </HeaderFrame>
          <HeaderFrame>
            <CreationHeader
              sticky={false}
              title="Choose game name"
              step={1}
              steps={3}
              onBack={() => {}}
              account={
                <Button variant="secondary" size="small">
                  Sign up
                </Button>
              }
            />
          </HeaderFrame>
          <HeaderFrame>
            <CreationHeader
              sticky={false}
              title="Choose game name"
              step={1}
              steps={3}
              onBack={() => {}}
            />
          </HeaderFrame>
        </Stack>
      </Specimens>

      <Specimens
        label="Long title"
        note="The centre column truncates. Sides stay put, so a long step name cannot shove the account off."
      >
        <Stack gap={4} wide>
          <HeaderFrame>
            <CreationHeader
              sticky={false}
              title="How you would like people to exchange gifts this year"
              step={2}
              steps={3}
              onBack={() => {}}
              account={<Account />}
            />
          </HeaderFrame>
        </Stack>
      </Specimens>
    </FoldPanel>
  );
}
